import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { Plus, Edit, Trash2, MoreVertical } from 'lucide-react';
import { jobTitleService, JobTitle } from '@/services/jobTitle.service';
import { useAuthStore } from '@/stores/authStore';
import { PERMISSIONS } from '@/types/permissions';

export default function JobTitleManagement() {
  const { hasPermission } = useAuthStore();

  // Check permissions
  const canCreateJobTitles = hasPermission(PERMISSIONS.JOB_TITLE_CREATE);
  const canEditJobTitles = hasPermission(PERMISSIONS.JOB_TITLE_EDIT);
  const canDeleteJobTitles = hasPermission(PERMISSIONS.JOB_TITLE_DELETE);

  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingJobTitle, setEditingJobTitle] = useState<JobTitle | null>(null);
  const [deletingJobTitle, setDeletingJobTitle] = useState<JobTitle | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isActive: true,
  });

  // Load job titles
  const loadJobTitles = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await jobTitleService.getJobTitles({
        page,
        limit: 10,
        search: search || undefined,
      });
      setJobTitles(response.data || []);
      setPagination(response.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      });
    } catch (error) {
      console.error('Error loading job titles:', error);
      toast({
        title: 'Error',
        description: 'Failed to load job titles',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobTitles();
  }, []);


  // Handle create
  const handleCreate = async () => {
    try {
      if (!formData.title.trim() || !formData.description.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          variant: 'destructive',
        });
        return;
      }

      await jobTitleService.createJobTitle(formData);
      toast({
        title: 'Success',
        description: 'Job title created successfully',
      });
      setIsCreateModalOpen(false);
      setFormData({ title: '', description: '', isActive: true });
      loadJobTitles(pagination.page);
    } catch (error: any) {
      console.error('Error creating job title:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create job title',
        variant: 'destructive',
      });
    }
  };

  // Handle edit
  const handleEdit = (jobTitle: JobTitle) => {
    if (!canEditJobTitles) {
      toast({
        title: "❌ Permission Denied",
        description: "You don't have permission to edit job titles",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    setEditingJobTitle(jobTitle);
    setFormData({
      title: jobTitle.title,
      description: jobTitle.description,
      isActive: jobTitle.isActive,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingJobTitle) return;

    try {
      if (!formData.title.trim() || !formData.description.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          variant: 'destructive',
        });
        return;
      }

      await jobTitleService.updateJobTitle(editingJobTitle.id, formData);
      toast({
        title: 'Success',
        description: 'Job title updated successfully',
      });
      setIsEditModalOpen(false);
      setEditingJobTitle(null);
      setFormData({ title: '', description: '', isActive: true });
      loadJobTitles(pagination.page);
    } catch (error: any) {
      console.error('Error updating job title:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update job title',
        variant: 'destructive',
      });
    }
  };

  // Handle delete
  const handleDelete = (jobTitle: JobTitle) => {
    if (!canDeleteJobTitles) {
      toast({
        title: "❌ Permission Denied",
        description: "You don't have permission to delete job titles",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    setDeletingJobTitle(jobTitle);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingJobTitle) return;

    try {
      await jobTitleService.deleteJobTitle(deletingJobTitle.id);
      toast({
        title: 'Success',
        description: 'Job title deleted successfully',
      });
      setIsDeleteDialogOpen(false);
      setDeletingJobTitle(null);
      loadJobTitles(pagination.page);
    } catch (error: any) {
      console.error('Error deleting job title:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete job title',
        variant: 'destructive',
      });
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ title: '', description: '', isActive: true });
    setEditingJobTitle(null);
  };

  return (

    <div className="space-y-4 p-4 pt-1 pb-10">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold">Job Titles</h3>
        <div>
        </div>
        {canCreateJobTitles && (
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Add Job Title
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create Job Title</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Software Engineer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the job title responsibilities"
                    rows={3}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                  <Label htmlFor="isActive">Active</Label>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate}>Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Job Title List</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">Job Title</TableHead>
                  <TableHead className="text-xs sm:text-sm">Description</TableHead>
                  <TableHead className="text-xs sm:text-sm">Status</TableHead>
                  <TableHead className="text-xs sm:text-sm">Created</TableHead>
                  <TableHead className="text-right text-xs sm:text-sm">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        <span className="text-sm text-muted-foreground">
                          Loading job titles...
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : jobTitles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <div className="flex flex-col items-center space-y-2">
                        <span className="text-sm text-muted-foreground">
                          No job titles found
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  jobTitles.map((jobTitle) => (
                    <TableRow key={jobTitle.id}>
                      <TableCell className="font-medium text-xs sm:text-sm">
                        {jobTitle.title}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm text-muted-foreground max-w-xs truncate">
                        {jobTitle.description}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-xs ${jobTitle.isActive
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                            }`}
                        >
                          {jobTitle.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm text-muted-foreground">
                        {new Date(jobTitle.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 sm:h-9 sm:w-9"
                            >
                              <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-[160px]">
                            {canEditJobTitles && (
                              <DropdownMenuItem onClick={() => handleEdit(jobTitle)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            {canDeleteJobTitles && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(jobTitle)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4">
              <div className="text-sm text-gray-500">
                Showing {((pagination?.page || 1) - 1) * (pagination?.limit || 10) + 1} to{' '}
                {Math.min((pagination?.page || 1) * (pagination?.limit || 10), pagination?.total || 0)} of{' '}
                {pagination?.total || 0} entries
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => loadJobTitles((pagination?.page || 1) - 1)}
                  disabled={(pagination?.page || 1) === 1}
                  className="h-8 px-3 text-sm"
                >
                  Previous
                </Button>
                <Button
                  variant="default"
                  className="h-8 w-8 p-0 text-sm bg-[#000000] hover:bg-[#000000] text-white"
                  disabled
                >
                  {pagination?.page || 1}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => loadJobTitles((pagination?.page || 1) + 1)}
                  disabled={(pagination?.page || 1) === (pagination?.totalPages || 1)}
                  className="h-8 px-3 text-sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {canEditJobTitles && (
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Edit Job Title</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Job Title *</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Software Engineer"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description *</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the job title responsibilities"
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="edit-isActive">Active</Label>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate}>Update</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {canDeleteJobTitles && (
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the job title
                "{deletingJobTitle?.title}" and remove it from the system.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
